import React, { useState, ChangeEvent, FormEvent } from "react";
import { motion } from "framer-motion";
import { globalTransition } from "../../assets/animate/globalAnimation";
import { Link } from "react-router-dom";

interface FormData {
  name: string;
  company: string;
  phone: string;
  email: string;
  message: string;
}

const ContactLoc: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    company: "",
    phone: "",
    email: "",
    message: "",
  });

  const [errors, setErrors] = useState<Partial<FormData>>({}); // State for validation errors
  const [isSubmitting, setIsSubmitting] = useState(false); // State for submission status

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required.";
    }
    if (!formData.company.trim()) {
      newErrors.company = "Company name is required.";
    }
    if (!formData.phone.trim() && !formData.email.trim()) {
      newErrors.phone = "Either phone number or email is required.";
      newErrors.email = "Either phone number or email is required.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    setErrors({
      ...errors,
      [name]: "", // Clear error for the field being edited
    });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("companyName", formData.company); 
      formDataToSend.append("phone", formData.phone);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("message", formData.message);
      formDataToSend.append("formType", "Partner Connect"); 
      const apiKey = process.env.API_KEY_VALUE;
      const headers = {
        "x-api-key": apiKey as string,
      };
      const response = await fetch(
        `${process.env.API_BASE_URL}/send-partner-connect`,
        {
          method: "POST",
          headers: headers,
          body: formDataToSend,
        }
      );



      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.errors) {
          const newErrors: Partial<FormData> = {};
          for (const [key, value] of Object.entries(errorData.errors)) {
            newErrors[key as keyof FormData] = value as string;
          }
          setErrors(newErrors); // Set server-side validation errors
        }
        throw new Error("Failed to submit the form");
      }
      alert("Your message has been sent successfully!");
      setFormData({
        name: "",
        company: "",
        phone: "",
        email: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting the form:", error);
      alert("There was an error submitting your message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full min-h-screen bg-slate-600 pb-20 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Left Section - Text */}
        <motion.div
          className="text-white p-4 flex flex-col justify-center"
          initial={{ opacity: 0, x: -100 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ ...globalTransition, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          <h1 className="text-3xl md:text-4xl font-bold mb-6">
            Contact Your Local Siskon ParkerStore
          </h1>
          <p className="text-lg text-gray-300">
            Let our location in Gujarat be your go-to local source for hose,
            fitting, hydraulic, pneumatic, and filtration products and services.
            <span className="underline font-semibold text-blue-400 cursor-pointer">
              <Link to="/contactUs">Connect with</Link>
            </span>{" "}
            one of our engineers or account managers to get started.
          </p>
        </motion.div>

        {/* Right Section - Form */}
        <motion.div
          className="px-1 lg:px-16"
          initial={{ opacity: 0, x: 100 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ ...globalTransition, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          <form
            onSubmit={handleSubmit}
            className="space-y-4 bg-white rounded-lg p-10 shadow-lg"
          >
            <div>
              <label
                htmlFor="name"
                className="block text-gray-800 font-medium mb-2"
              >
                First & Last Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full border ${
                  errors.name ? "border-red-500" : "border-gray-300"
                } rounded p-2`}
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="company"
                className="block text-gray-800 font-medium mb-2"
              >
                Company Name
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className={`w-full border ${
                  errors.company ? "border-red-500" : "border-gray-300"
                } rounded p-2`}
              />
              {errors.company && (
                <p className="text-red-500 text-sm mt-1">{errors.company}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="phone"
                className="block text-gray-800 font-medium mb-2"
              >
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`w-full border ${
                  errors.phone ? "border-red-500" : "border-gray-300"
                } rounded p-2`}
              />
              {errors.phone && (
                <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-gray-800 font-medium mb-2"
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full border ${
                  errors.email ? "border-red-500" : "border-gray-300"
                } rounded p-2`}
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="message"
                className="block text-gray-800 font-medium mb-2"
              >
                How can we help?
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Type your message"
                className="w-full border border-gray-300 rounded max-h-[30vh] p-2 h-24"
              />
            </div>

            <button
              type="submit"
              className="w-full bg-red-700 hover:bg-red-800 text-white font-medium py-3 px-4 rounded transition"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default ContactLoc;